import React from 'react';
import { makeStyles, tokens, FluentProvider, webLightTheme } from '@fluentui/react-components';
import OfficeHeader from './OfficeHeader';
import { llmService, LLMEnhancementService } from '../../../../llm/services/LLMEnhancementService';
import { StyleProvider } from './StyleContext';

// Context for LLM state
interface LLMContextType {
  isLLMEnabled: boolean;
  isLLMAuthenticated: boolean;
  llmService?: LLMEnhancementService;
}

export const LLMContext = React.createContext<LLMContextType>({
  isLLMEnabled: false,
  isLLMAuthenticated: false,
  llmService: undefined
});

export const useLLMContext = () => React.useContext(LLMContext);

interface OfficeAppPropsOld {
  appName: 'Word' | 'PowerPoint' | 'OneNote' | 'Excel';
  documentTitle?: string;
  children: React.ReactNode;
  showHeader?: boolean;
  customRibbon?: React.ReactNode;
  headerFixed?: boolean;
  headerBackgroundColor?: string;
  statusBar?: React.ReactNode;
  navSection?: React.ReactNode;
  showSearchBox?: boolean;
  // LLM props to pass to children
  isLLMEnabled?: boolean;
  isLLMAuthenticated?: boolean;
}

interface OfficeAppPropsNew {
  appType: 'forms' | string;
  documentTitle?: string;
  appBody?: React.ReactNode;
  showHeader?: boolean;
  customRibbon?: React.ReactNode;
  headerFixed?: boolean;
  headerBackgroundColor?: string;
  statusBar?: React.ReactNode;
  navSection?: React.ReactNode;
  showSearchBox?: boolean;
  showCopilot?: boolean;
  showStatusBar?: boolean;
}

type OfficeAppProps = OfficeAppPropsOld | OfficeAppPropsNew;

const useStyles = makeStyles({
  container: {
    width: '100vw',
    height: '100vh',
    backgroundColor: tokens.colorNeutralBackground3,
    display: 'flex',
    flexDirection: 'column',
  },
  containerWithFixedHeader: {
    paddingTop: '48px', // Account for fixed header height
  },
  header: {
    flexShrink: 0,
  },
  ribbon: {
    flexShrink: 0,
    margin: 0, // Explicitly remove any default margins
    padding: 0, // Explicitly remove any default padding
  },
  navSection: {
    flexShrink: 0,
  },
  body: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  statusBar: {
    flexShrink: 0,
  },
});

function OfficeApp(props: OfficeAppProps) {
  const classes = useStyles();

  // Type guard to determine which props interface we're using
  const isNewProps = 'appType' in props;
  
  const appName = isNewProps ? 
    (props.appType.charAt(0).toUpperCase() + props.appType.slice(1)) as 'Word' | 'PowerPoint' | 'OneNote' | 'Excel' | 'Forms' :
    props.appName;
  const documentTitle = props.documentTitle;
  const showHeader = props.showHeader ?? true;
  const customRibbon = props.customRibbon;
  const headerFixed = props.headerFixed ?? false;
  const headerBackgroundColor = props.headerBackgroundColor;
  const statusBar = isNewProps ? (props.showStatusBar ? props.statusBar : null) : props.statusBar;
  const navSection = props.navSection;
  const showSearchBox = props.showSearchBox ?? true;
  const children = isNewProps ? props.appBody : props.children;

  const containerClassName = headerFixed 
    ? `${classes.container} ${classes.containerWithFixedHeader}`
    : classes.container;

  // LLM state management
  const [llmEnabled, setLlmEnabled] = React.useState(false);
  const [llmAuthenticated, setLlmAuthenticated] = React.useState(false);

  // Load persisted LLM state and check authentication status when component mounts
  React.useEffect(() => {
    const initializeLLMState = async () => {
      try {
        // Get persisted config
        const config = llmService.getConfig();
        setLlmEnabled(config.isEnabled);
        setLlmAuthenticated(config.isAuthenticated);
        
        // Verify current authentication status
        const currentAuthStatus = await llmService.isAuthenticated();
        if (currentAuthStatus !== config.isAuthenticated) {
          // Update if authentication status has changed
          llmService.updateConfig({ isAuthenticated: currentAuthStatus });
          setLlmAuthenticated(currentAuthStatus);
        }
      } catch (error) {
        console.warn('Failed to initialize LLM state:', error);
      }
    };
    
    initializeLLMState();
  }, []);

  const handleLLMToggleChange = React.useCallback(async (enabled: boolean, isAuthenticated: boolean) => {
    setLlmEnabled(enabled);
    setLlmAuthenticated(isAuthenticated);
    
    llmService.updateConfig({
      isEnabled: enabled,
      isAuthenticated: isAuthenticated
    });
  }, []);

  return (
    <FluentProvider theme={webLightTheme}>
      <StyleProvider>
        <div className={containerClassName}>
          {showHeader && (
            <div className={classes.header}>
              <OfficeHeader 
                appName={appName}
                documentTitle={documentTitle}
                isFixed={headerFixed}
                backgroundColor={headerBackgroundColor}
                avatarName="Adele Vance"
                avatarImage="/src/assets/avatars/AdeleV.jpg"
                showSearchBox={showSearchBox}
                onLLMToggleChange={handleLLMToggleChange}
              />
            </div>
          )}
          
          {customRibbon && (
            <div className={classes.ribbon}>
              {customRibbon}
            </div>
          )}
          
          {navSection && (
            <div className={classes.navSection}>
              {navSection}
            </div>
          )}
          
          <div className={classes.body}>
            <LLMContext.Provider value={{ isLLMEnabled: llmEnabled, isLLMAuthenticated: llmAuthenticated, llmService }}>
              {children}
            </LLMContext.Provider>
          </div>
          
          {statusBar && (
            <div className={classes.statusBar}>
              {statusBar}
            </div>
          )}
        </div>
      </StyleProvider>
    </FluentProvider>
  );
}

export default OfficeApp;
